import React, { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';

import { DynamicIconWrapper, Icons, Tooltip } from '@components/common';
import ChangedField from '@components/common/ChangedField';
import TextAvatar from '@components/common/TextAvatar/TextAvatar';

import { useQueryAssetTypes } from '@hooks/assets/useQueryAssetTypes';

import { auth } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { viewOnMap } from '@slices/map';

import { dateFormattingWithSlash, formatDate } from '@utils/date';
import { checkGroupPanelAccess } from '@utils/permissions';

import { TAssetProfile } from '@interfaces/assets/asset';
import { TChangedAssetProfile, TFieldItemType } from '@interfaces/assets/assetHistory';

import { mapGroupAccess } from '@constants/menuGroupAccess';
import { routes } from '@constants/routes';
import { dateDescriptionFields, FIRST_VERSION, nameSectionFields } from '../constants';
import { viewOnMapTooltip } from '@components/routes/AdminPanel/AssetProfile/constants';

import { getField } from '../util';

import styles from './ProfileHeader.module.scss';

//export for testing purposes
export interface IFieldValue {
	className?: string;
	current: ReactNode;
	fieldTitle?: string;
	isChanged?: boolean;
	previous?: ReactNode;
	prefix?: ReactNode;
	testId?: string;
}

//export for testing purposes
export const FieldValue = (field: IFieldValue) => {
	const wrapperClassName = field?.className || '';

	return (
		<div className={cn({ [styles[wrapperClassName]]: field?.className }, styles.flexWrapper)}>
			<ChangedField {...field} />
		</div>
	);
};

const { map } = routes;

interface ProfileHeaderProps {
	profile: TChangedAssetProfile;
	version: Record<string, any>;
	relevantProfile: TAssetProfile;
}

const ProfileHeader = ({ profile, version, relevantProfile }: ProfileHeaderProps) => {
	const showOnMap = profile?.showOnMap;
	const createdAt = formatDate(new Date(relevantProfile.createdAt));

	const updatedAt = {
		current: dateFormattingWithSlash(version?.current),
		previous: version?.previous ? dateFormattingWithSlash(version?.previous) : null,
	};

	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const { accessPermission } = useAppSelector(auth);

	const isMapModuleAvailable = checkGroupPanelAccess(accessPermission, mapGroupAccess);

	const getTextShowOnMap = (showOnMap: boolean) => showOnMap ? 'On' : 'Off';

	const handleViewOnMap = () => {
		if (!relevantProfile?.showOnMap) return;

		void dispatch(viewOnMap({ marker: relevantProfile }));
		navigate(map.index, { state: { isRedirectedFromAdminPanel: true } });
	};

	const { assetTypesSubtypes } = useQueryAssetTypes({ isAssetTypesSubtypesWithIconUrl: false });

	const assetTypeId = assetTypesSubtypes.find(({ value }) => relevantProfile?.assetType.id === value)?.type?.icon?.id;

	return (
		<div className={styles.header}>
			<div className={cn(styles.whiteWrapper, styles.nameSection)}>
				<div className={styles.icon}>
					<TextAvatar
						avatarId={assetTypeId}
						fontSize='20px'
						isProfileLogo
						isUnknown={!assetTypeId}
						size='75px'
					/>
				</div>
				<div>
					{nameSectionFields.map((item: TFieldItemType, key) => {
						const field = getField(profile, item.fieldName as string);

						return field ? (
							<FieldValue
								key={`${item.className}_${key}`}
								className={item.className}
								current={field?.current}
								fieldTitle={item.fieldTitle}
								isChanged={!!field?.isChanged}
								prefix={item.prefix}
								previous={field?.previous}
								testId={field?.testId as string}
							/>
						) : null;
					})}
					<div className={styles.version}>{`Version ${version.currentVersion || FIRST_VERSION}`}</div>
				</div>
				{isMapModuleAvailable && (
					<div className={styles.location}>
						<Tooltip content={viewOnMapTooltip}>
							<div onClick={handleViewOnMap}>
								<DynamicIconWrapper
									icon={Icons.location}
									isDisabled={!relevantProfile?.showOnMap}
								/>
							</div>
						</Tooltip>
					</div>
				)}
			</div>
			<div className={cn(styles.whiteWrapper, styles.assetOnMapSection)}>
				<FieldValue
					className='mapStatus'
					current={getTextShowOnMap(!!showOnMap?.current)}
					fieldTitle='Show Asset on the Map:'
					isChanged={!!profile?.showOnMap?.isChanged}
					previous={getTextShowOnMap(!!showOnMap?.previous)}
					testId='showOnMap'
				/>
				<FieldValue
					className='date'
					current={updatedAt.current}
					fieldTitle='Last record update:'
					isChanged={true}
					previous={updatedAt.previous}
					testId='updatedAt'
				/>
				{dateDescriptionFields.map((item: TFieldItemType, key) => {
					const field = getField(profile, item.fieldName as string);

					if (!field) return null;

					const currentDate = field?.current ? formatDate(new Date(field?.current as string)) : null;
					const previousDate = field?.previous ? formatDate(new Date(field?.previous as string)) : null;

					return (
						<FieldValue
							key={`${field?.current}_${item?.fieldTitle}_${key}}`}
							{...field}
							{...item}
							className='date'
							current={currentDate}
							previous={previousDate}
						/>
					);
				})}
				<FieldValue
					className='date'
					current={createdAt}
					fieldTitle='Created:'
					previous={null}
				/>
			</div>
		</div>
	);
};

export default ProfileHeader;
